import React from "react";
import classNames from "classnames";
import { StoryblokAsset } from "../../../interfaces/Storyblok/StoryblokAsset.interface";
import Image from "../../atoms/Image/Image.atom";
import Crumb from "../../atoms/Crumb/Crumb.atom";
import "animate.css/animate.min.css";
import "./LogoGallery.css";
import BlokWithBorderIcons from "../../../interfaces/Storyblok/BlokWithBorderIcons.interface";
import BorderIconsFactory from "../../utils/BorderIconsFactory/BorderIconsFactory.util";
import { BorderIconType } from "../../utils/BorderIconsFactory/BorderIcon.util";

interface LogoGalleryProps extends BlokWithBorderIcons {
  heading: string;
  body: string;
  logos: StoryblokAsset[];
  crumb: string;
  /** When true, show the logos on a single infinitely-scrolling line instead of the auto (max 6-column) grid. */
  force_one_line?: boolean;
  /** When true, use the dark brand background (Primary-900) with white text; otherwise a white background. */
  use_brand_background?: boolean;
  /** When true (and force_one_line is false), centre-align the logos instead of the default left-aligned grid. */
  is_centered?: boolean;
  /** Optional anchor id so other components can deep-link/scroll to this section (e.g. a CTA to `/page#partners`). */
  id?: string;
}

const LogoTile: React.FC<{ image: StoryblokAsset }> = ({ image }) => (
  <div className="bg-White rounded-6xl overflow-hidden p-4">
    <div className="relative h-28">
      <span className="absolute top-0 right-0 bottom-0 left-0 m-auto flex items-center justify-center">
        <Image image={image} objectFit="contain" />
      </span>
    </div>
  </div>
);

const LogoGallery: React.FC<LogoGalleryProps> = ({
  heading,
  body,
  logos = [],
  crumb,
  id,
  force_one_line = false,
  use_brand_background = false,
  is_centered = false,
  border_icon_top_left,
  border_icon_top_right,
  border_icon_bottom_left,
  border_icon_bottom_right,
}) => {
  return (
    <div
      id={id}
      className={classNames("scroll-mt-24 lg:scroll-mt-32", use_brand_background ? "bg-Primary-900" : "bg-White")}
    >
      <BorderIconsFactory
        type={BorderIconType.Blok}
        border_icon_top_left={border_icon_top_left}
        border_icon_top_right={border_icon_top_right}
        border_icon_bottom_left={border_icon_bottom_left}
        border_icon_bottom_right={border_icon_bottom_right}
      >
        <div className="container mx-auto py-12 md:py-16 lg:py-20 z-10">
          <div className="text-center w-10/12 mx-auto">
            {crumb && <Crumb label={crumb} variant={use_brand_background ? "light" : "brand"} className="mb-4" />}
            {heading && (
              <h4
                className={classNames("font-extrabold text-2xl md:text-3xl xl:text-4x mb-6", {
                  "text-White": use_brand_background,
                  "text-Primary-900": !use_brand_background,
                })}
              >
                {heading}
              </h4>
            )}
            <div className={classNames("mb-12", use_brand_background ? "text-White" : "text-[#4D4D4D]")}>{body}</div>
          </div>

          {force_one_line ? (
            // Single line: logos scroll horizontally forever, ~6 visible at a time.
            // The list is duplicated so the -50% translate loops seamlessly.
            <div className="logo-marquee">
              <div className="logo-marquee__track">
                {[...logos, ...logos].map((logo, index) => (
                  <div key={index} className="shrink-0 w-48 px-3 xl:w-52">
                    <LogoTile image={logo} />
                  </div>
                ))}
              </div>
            </div>
          ) : is_centered ? (
            // Centred: logos wrap and stay centre-aligned, up to 6 per row.
            <div className="flex flex-wrap justify-center gap-6">
              {logos.map((logo, index) => (
                <div
                  key={index}
                  className="w-full sm:w-[calc((100%-1.5rem)/2)] lg:w-[calc((100%-4.5rem)/4)] xl:w-[calc((100%-7.5rem)/6)]"
                >
                  <LogoTile image={logo} />
                </div>
              ))}
            </div>
          ) : (
            // Auto: static grid, up to 6 columns, wrapping to more rows as needed.
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-6">
              {logos.map((logo, index) => (
                <div key={index} className="col-span-1">
                  <LogoTile image={logo} />
                </div>
              ))}
            </div>
          )}
        </div>
      </BorderIconsFactory>
    </div>
  );
};

export default LogoGallery;
